/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {EntityTreeBranchModel} from '../models/entity-tree-branch.model';
import {EntityElementModel} from '../entity-element/models/entity-element.model';
import {EntityTreeUtilService} from '../services/entity-tree-util.service';
import {AppService} from '../../../app.service';
import {Subscription, timer} from 'rxjs';
import {ContextMenuTypeEnum} from '../entity-element/models/context-menu-type.enum';
import {ContextMenuActionModel} from '../entity-element/models/context-menu-action.model';
import {ContextMenuActionEnum} from '../entity-element/models/context-menu-action.enum';
import {CdkDragDrop, CdkDropList} from '@angular/cdk/drag-drop';
import {EntityElementTypeEnum} from '../entity-element/models/entity-element-type.enum';
import {EntityElementDropEventModel} from '../entity-element/models/entity-element-drop-event.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {BusinessSettingsModal} from '../../../settings/business-settings/shared/business-settings-modal';
import {take} from 'rxjs/operators';
import {EntityTreeDisplayModeEnum} from '../../../settings/business-settings/models/entity-tree-display-mode.enum';
import {EntityElementPathStateModel} from '../entity-element/models/entity-element-path-state.model';
import {EntityElementWarningsModel} from '../entity-element/models/entity-element-warnings.model';
import {WarningsEnum} from '../../warnings/warnings.enum';
import {EntityTreeCssMarkersType} from '../entity-tree.css.markers.type';
import {ConfirmModalComponent} from '../../confirm-modal/confirm-modal/confirm-modal.component';
import {ConfirmModalConfigurationModel} from '../../confirm-modal/confirm-modal/models/confirm-modal-configuration.model';
import {AlertNotificationSeverity} from '../../../alert-notification-severity.model';
import {ConfirmModalBodyModel} from '../../confirm-modal/confirm-modal/models/confirm-modal-body.model';
import {ConfirmModalManualChecksModel} from '../../confirm-modal/confirm-modal/models/confirm-modal-manual-checks.model';
import {ToastWrapperService} from '../../utils/toast-wrapper-service/toast-wrapper-service';
import {CloseContextMenuService} from '../../context-menu/services/close-context-menu.service';
import {DragAndDropGridItemModel} from '../../drag-and-drop-grid/drag-and-drop-grid-item.model';
import {EntityCodeTypeModel} from '../models/entity-code-type.model';
import {EntityTreeBranchDisplayDataModel} from '../../../settings/business-settings/configuration/v1/models/entity-tree-branch-display-data.model';
import {TreeNodeModel} from '../../../settings/business-settings/models/tree-node-model';

@Component({
    selector: 'app-entity-tree-node',
    templateUrl: './entity-tree-node.component.html',
    styleUrls: ['./entity-tree-node.component.css', '../entity-tree.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EntityTreeNodeComponent implements OnDestroy, AfterViewChecked, OnInit {
    @Input() configurationTreeNode: TreeNodeModel;

    @Input() openEditEntityModal = new EventEmitter<EntityCodeTypeModel>();
    @Input() displayMode: EntityTreeDisplayModeEnum;
    @Input() entityNode: EntityTreeBranchModel = {} as EntityTreeBranchModel;
    @Input() entityNodeDisplayData: EntityTreeBranchDisplayDataModel = {} as EntityTreeBranchDisplayDataModel;
    @Input() pathConnectorClass: string;
    @Input() pathToConnectionsConnectorClass: string;
    @Input() hasEntityConnector: boolean;
    @Input() hasPathNodeConnector: boolean;
    @Input() hasClosingConnectionsConnector: boolean;
    @Input() enableEntity: boolean;
    @Input() hasSingleConnection: boolean;
    @Input() isSpecificBranch: boolean;
    @Input() entityPathState: EntityElementPathStateModel;
    @Input() isInOriginalPosition: boolean;
    @Output() addToPool = new EventEmitter<EntityElementModel>();
    @Output() removeFromPool = new EventEmitter<EntityElementModel>();
    @Output() editPoolItem = new EventEmitter<EntityElementModel>();
    @Output() addToConnectionsClick = new EventEmitter<EntityElementModel>();
    @Output() addToPathClick = new EventEmitter<EntityElementModel>();
    @Output() removeFromPathClick = new EventEmitter<EntityElementModel>();
    @Output() removeFromConnectionsClick = new EventEmitter<EntityElementModel>();
    @Output() skipEntityClick = new EventEmitter<boolean>();
    @Output() changePoolItemsOrder = new EventEmitter<EntityElementModel[]>();
    @Input() structureClass: string;
    @ViewChild('entityPoolContainer') entityPoolContainer: ElementRef;
    @ViewChild('entityConnectionsContainer') entityConnectionsContainer: ElementRef;
    @Output() emitParent: EventEmitter<EntityElementModel> = new EventEmitter();

    EntityTreeDisplayModeEnum = EntityTreeDisplayModeEnum;
    // Variable for the drag and drop
    public poolConnectedList: CdkDropList;
    // Variables for the accordion behaviour
    public hasViewMore: boolean;
    public hasViewLess: boolean;
    public isOpen: boolean;
    public ContextMenuTypeEnum = ContextMenuTypeEnum;
    public EntityElementTypeEnum = EntityElementTypeEnum;
    public readonly markerType = EntityTreeCssMarkersType;
    public readonly UNFINISHED_PATH_MARKER_BASE_CLASS = 'unfinished-path-origin';
    public readonly UNFINISHED_PATH_MARKER_BASE_CONNECTIONS_CLASS = 'unfinished-path-origin-connections';
    public readonly UNFINISHED_DRAG_DROP_MESSAGE_VIEW_MODE = 'In order to add a connection proceed to the edit mode';
    // The component max height
    private readonly DEFAULT_ENTITY_CONTAINER_MAX_HEIGHT = 261;
    // Width of an entity-element plus its padding (124px - 8px * 2).
    private readonly WIDTH_OF_ONE_ELEMENT = 140;
    // Height of an entity-element plus its padding (45px - 8px * 2).
    private readonly HEIGHT_OF_ONE_ELEMENT = 61;
    // Padding inside the entity-elements containers, i.e., the pool and the connections container.
    private readonly CONTAINERS_PADDING = 16;
    // Compensation value for the pool container margin
    private readonly POOL_CONTAINER_MARGIN = 1;
    // The component max height + button height
    private readonly ENTITY_CONTAINER_MAX_HEIGHT = 272;
    private MAX_NUMBER_OF_LINES = 4;
    private readonly ADD_ENTITY_ANIMATION_TIMEOUT = 6000;
    private subscriptions: Subscription = new Subscription();

    public get topPathConnectorClass(): string {
        let topPathConnectorClass = `path-connector-top ${this.pathConnectorClass}`;

        if (this.entityNodeDisplayData.representParent) {
            topPathConnectorClass = this.getRepresentableChildPathConnectorClass(topPathConnectorClass);
        } else {
            if (this.isInUnfinishedPath() && (!this.isUnfinishedPathOrigin() || this.entityHasOnlyConnections())) {
                topPathConnectorClass += ' unfinished-path-connector';

            } else if (this.isInInactivePath()) {
                topPathConnectorClass += ' inactive-path-connector';
            }
        }

        return topPathConnectorClass;
    }

    public get bottomPathConnectorClass(): string {
        let bottomPathConnectorClass = `path-connector-bottom ${this.pathConnectorClass}`;

        if (this.entityNodeDisplayData.representParent) {
            bottomPathConnectorClass = this.getRepresentableChildPathConnectorClass(bottomPathConnectorClass);

        } else {
            if (this.isInUnfinishedPath()) {
                bottomPathConnectorClass += ' unfinished-path-connector';

            } else if ((this.isInInactivePath() || this.isInactivePathOrigin()) && !this.entityHasOnlyConnections()) {
                bottomPathConnectorClass += ' inactive-path-connector';
            }
        }
        return bottomPathConnectorClass;
    }

    public get finalPathToConnectionsConnectorClass(): string {
        let pathToConnectionsConnectorClass = this.pathToConnectionsConnectorClass;

        if (this.isInUnfinishedPath()) {
            if (!this.isUnfinishedPathOrigin() || (this.entityHasOnlyConnections && !this.entityHasSelectedConnections())) {
                pathToConnectionsConnectorClass += ' unfinished-path-connector';

            } else if (this.isInInactivePath()) {
                pathToConnectionsConnectorClass += ' inactive-path-connector';
            }
        } else if (this.isInInactivePath()) {
            pathToConnectionsConnectorClass += ' inactive-path-connector';
        }

        return pathToConnectionsConnectorClass;
    }

    public get dropzoneClass(): string {
        let dropzoneContainerClass = 'drop-zone';

        if (this.isInUnfinishedPath()
                && this.entityNodeDisplayData.hasConnections
                && !this.entityHasSelectedConnections()) {
            dropzoneContainerClass += ' unfinished-path-connector';

            if (this.displayMode === EntityTreeDisplayModeEnum.VIEW) {
                dropzoneContainerClass += ' unfinished-path-connector-view';
            }
        }

        return dropzoneContainerClass;
    }

    public get hasUnfinishedPathOriginMarker(): boolean {
        let hasMarker = false;

        if (this.enableEntity &&
                ((this.isUnfinishedPathOrigin() && !this.entityNodeDisplayData.representParent)
                        || this.representableChildParentHasUnfinishedPathOrigin())) {
            hasMarker = true;
            if (this.entityNodeDisplayData.hasPath) {
                hasMarker = this.entityNode.path != null;
            }
        }
        return hasMarker;
    }

    public get unfinishedPathOriginClass(): string {
        let unfinishedPathOriginClass = this.UNFINISHED_PATH_MARKER_BASE_CLASS;

        if (this.entityHasOnlyConnections()) {
            unfinishedPathOriginClass += ' no-path';
        }

        if (this.isInInactivePath()) {
            unfinishedPathOriginClass += ' inactive-path-connector';
        }

        if (!this.isInOriginalPosition) {
            unfinishedPathOriginClass += ' not-original-order';
        }

        return unfinishedPathOriginClass;
    }

    public get hasUnfinishedPathOriginConnectionsMarker(): boolean {
        let hasMarker = false;

        if (this.enableEntity && this.isInUnfinishedPath()
                && this.entityHasOnlyConnections()
                && !this.entityNode.skippable
                && this.pathConnectorClass === 'full') {
            hasMarker = !this.entityHasSelectedConnections();
        }

        return hasMarker;
    }

    /**
     * Return the correct type of context menu for the pool section
     * When there is no connection section, the pool section must function as the connections section
     * @return ContextMenuTypeEnum
     */
    public get poolContextMenuType(): ContextMenuTypeEnum {
        let menuType: ContextMenuTypeEnum = ContextMenuTypeEnum.EMPTY;

        if (this.entityNodeDisplayData.hasPath) {
            if (this.entityNodeDisplayData.hasConnections) {
                menuType = ContextMenuTypeEnum.PATH_CONNECTIONS_POOL;
            } else {
                menuType = ContextMenuTypeEnum.PATH_POOL;
            }
        } else if (this.entityNodeDisplayData.hasConnections) {
            menuType = ContextMenuTypeEnum.CONNECTIONS_POOL;
        }

        return menuType;
    }

    get pathNodeDropZoneId(): string {
        return `pathNodeDropZone-${this.entityNode.entityCode}`;
    }

    get connectionsDropZoneId(): string {
        return `connectionsDropZone-${this.entityNode.entityCode}`;
    }

    get poolDropZoneId(): string {
        return `poolDropZone-${this.entityNode.entityCode}`;
    }

    get containerNodeDataCy(): string {
        return `entity-tree-node-container-${this.entityNode.entityCode}`;
    }

    get containerNameDataCy(): string {
        return `entity-tree-node-container-name-${this.entityNode.entityCode}`;
    }

    get contentNodeDataCy(): string {
        return `entity-tree-node-content-${this.entityNode.entityCode}`;
    }

    get pathNodeDataCy(): string {
        return `entity-tree-node-path-${this.entityNode.entityCode}`;
    }

    get connectionsDataCy(): string {
        return `entity-tree-node-connections-${this.entityNode.entityCode}`;
    }

    get poolDataCy(): string {
        return `entity-tree-node-pool-${this.entityNode.entityCode}`;
    }

    get addButtonDataCy(): string {
        return `entity-tree-node-add-button-${this.entityNode.entityCode}`;
    }

    get cutButtonDataCy(): string {
        return `entity-tree-node-cut-button-${this.entityNode.entityCode}`;
    }

    get hasAddButton(): boolean {
        return this.displayMode === EntityTreeDisplayModeEnum.EDIT
                && this.entityNode.skippable && !this.enableEntity && this.hasEntityConnector;
    }

    get hasCutButton(): boolean {
        return this.displayMode === EntityTreeDisplayModeEnum.EDIT
                && this.entityNode.skippable && this.enableEntity;
    }

    get globalClass(): string {
        let globalClass = 'entity-container';

        if (this.isSpecificBranch) {
            globalClass += ' specific-branch';
        }

        if (this.displayMode != null) {
            globalClass += ` ${this.displayMode}`;
        }

        return globalClass;
    }

    get entityTreeNodeMaxHeight(): { [key: string]: any } {
        // max-height with connections
        const maxHeightWithConnections = {'max-height.px': this.entityConnectionsMaxHeight()};
        // auto-height (Pool open)
        const autoHeight = {'max-height': 'unset'};

        return this.isOpen ? autoHeight : maxHeightWithConnections;
    }

    /**
     * Toggles the drag and drop text area class on the path and connections
     */
    public get dropZoneMessageClass(): string {
        let defaultClass = 'drop-zone-message';

        if (this.displayMode === EntityTreeDisplayModeEnum.VIEW && this.isInUnfinishedPath()) {
            defaultClass += ' unfinishedPathConnectionMessage';
        }
        return defaultClass;
    }

    public get unfinishedPathOriginConnectionsClass(): string {
        let unfinishedPathOriginClass = this.UNFINISHED_PATH_MARKER_BASE_CONNECTIONS_CLASS;

        if (!this.isInOriginalPosition) {
            unfinishedPathOriginClass += ' not-original-order';
        }

        return unfinishedPathOriginClass;
    }

    private get poolLength(): number {
        let poolLength = this.entityNode.pool?.length ?? 0;
        if (this.displayMode === EntityTreeDisplayModeEnum.EDIT) {
            poolLength = poolLength + 1;
        }
        return poolLength;
    }

    constructor(public entityUtilService: EntityTreeUtilService,
                private appService: AppService,
                private modal: NgbModal,
                private toastService: ToastWrapperService,
                private closeMenusService: CloseContextMenuService) {
    }

    ngOnInit(): void {
        this.subscriptions.add(this.openEditEntityModal.subscribe((entity: EntityCodeTypeModel) => {
            if (entity.type === this.entityNode.name) {
                const element: EntityElementModel = this.entityNode.pool.find((item) => item.code === entity.code);
                this.openEditModal(element, this.entityNode);
            }
        }));
    }

    closeContextMenus(): void {
        this.closeMenusService.closeAll();
    }

    public getInactiveElementWarnings(element: EntityElementModel): EntityElementWarningsModel {
        let warnings = {} as EntityElementWarningsModel;

        if (this.entityHasOnlyConnections() && !element.active && this.entityNode.invalidElementWarning != null) {
            warnings = {labels: [this.entityNode.invalidElementWarning], severity: WarningsEnum.WARNING};
        }

        return warnings;
    }

    public unfinishedPathOriginLineClass(baseClass: string): string {
        let lineClass = `${baseClass}-line`;

        if ((this.isInUnfinishedPath() || this.representableChildParentHasUnfinishedPathOrigin())
                && (this.isInactivePathOrigin() || this.isInInactivePath())) {
            lineClass += ' inactive-path-connector';
        }

        return lineClass;
    }

    ngAfterViewChecked(): void {
        this.toggleViewMore();
        this.toggleViewLess();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    skipEntity(skip: boolean): void {
        this.skipEntityClick.emit(skip);
    }

    /**
     * Manages the context menu clicks
     * @param actionModel The {@link ContextMenuActionModel} of the clicked action
     */
    contextMenuClick(actionModel: ContextMenuActionModel): void {
        switch (actionModel.action) {
            case ContextMenuActionEnum.PUT_IN_PATH:
                this.addEntityToPath(actionModel.element);
                break;
            case ContextMenuActionEnum.CONNECT:
                this.addEntityToConnections(actionModel.element);
                break;
            case ContextMenuActionEnum.EDIT:
                this.openEditModal(actionModel.element, this.entityNode);
                break;
            case ContextMenuActionEnum.DELETE:
                this.openDeleteModal(actionModel.element, this.entityNode);
                break;
        }
        // Add other emitters as actions are implemented
    }

    removeFromPath(element: EntityElementModel): void {
        this.removeFromPathClick.emit(element);
    }

    removeFromConnections(element: EntityElementModel): void {
        this.removeFromConnectionsClick.emit(element);
    }

    /**
     * Manages the click on the view more arrow
     */
    clickViewMore(): void {
        this.isOpen = true;
    }

    /**
     * Manages the click on the view less arrow
     */
    clickViewLess(): void {
        this.isOpen = false;
    }

    /**
     * Returns the entity container class according to the isOpen variable
     * @return string the entity container class
     */
    entityContainerHeightClass(): string {
        if (this.isOpen) {
            return 'entity-container auto-height';
        }
        return 'entity-container max-height';
    }

    /**
     * Determines to which drop zone should the pool drop list be connected to
     * @return string the drop zone id
     */
    poolDropListConnectedTo(): string {
        let zone: string;

        if (this.displayMode === EntityTreeDisplayModeEnum.EDIT || this.entityNode.isRoot) {
            if (this.entityNodeDisplayData.hasConnections) {
                zone = this.connectionsDropZoneId;
            } else {
                zone = this.pathNodeDropZoneId;
            }
        }

        return zone;
    }

    /**
     * Determines to which drop zone should the connection drop list be connected to
     * @return string the drop zone id
     */
    connectionsDropListConnectedTo(): string[] {
        const connectTo: string[] = [];

        if (this.displayMode === EntityTreeDisplayModeEnum.EDIT || this.entityNode.isRoot) {
            connectTo.push(this.poolDropZoneId);
        }

        if (this.entityNodeDisplayData.hasPath) {
            connectTo.push(this.pathNodeDropZoneId);
        }
        return connectTo;
    }

    /**
     * Determines to which drop zone should the connection drop list be connected to
     * @return string the drop zone id
     */
    pathDropListConnectedTo(): string[] {
        const connectTo: string[] = [this.connectionsDropZoneId];

        if (this.displayMode === EntityTreeDisplayModeEnum.EDIT || this.entityNode.isRoot) {
            connectTo.push(this.poolDropZoneId);
        }

        return connectTo;
    }

    /**
     * Manages the drop event in the path node section
     */
    dropInPath(event: CdkDragDrop<EntityElementDropEventModel>): void {
        if (this.enableEntity) {
            if (event.previousContainer !== event.container) {
                this.addEntityToPath(event.item.data.value);
            }
        }
    }

    /**
     * Manages the drop event in the connections section
     */
    dropInConnections(event: CdkDragDrop<EntityElementDropEventModel>): void {
        if (this.enableEntity) {
            if (event.previousContainer !== event.container) {
                if (event.item.data.origin === EntityElementTypeEnum.POOL) {
                    if (this.entityNode.connections.find((item) => item.code === event.item.data.value.code) == null) {
                        this.addEntityToConnections(event.item.data.value);
                    }
                } else if (event.item.data.origin === EntityElementTypeEnum.PATH) {
                    this.removeFromPath(event.item.data.value);
                }
            }
        }
    }

    /**
     * Manages the drop event in the pool section
     */
    dropInPool(event: CdkDragDrop<EntityElementDropEventModel>): void {
        if (this.enableEntity) {
            if (event.previousContainer !== event.container) {
                if (event.item.data.origin === EntityElementTypeEnum.CONNECTION) {
                    this.removeFromConnections(event.item.data.value);
                } else if (event.item.data.origin === EntityElementTypeEnum.PATH) {
                    this.removeFromPath(event.item.data.value);
                }
            }
        }
    }

    //  More information about why this is needed: https://angular.io/api/common/NgForOf#change-propagation
    /**
     * This method allows the change detection on ngFor over EntityElementModel[] to be tracked by the property contextMenuActions
     */
    trackByContextMenuActions(index: number,
                              item: DragAndDropGridItemModel<EntityElementDropEventModel>): ContextMenuActionEnum[] {
        return item.data.value.contextMenuActions;
    }

    /**
     * Determines if the drag for a specific pool item should be disabled or not
     * @param element The element to check if can be dragged
     * @return boolean
     */
    disableDragForPoolItem(element: EntityElementModel): boolean {
        let disable = true;
        if (this.displayMode === EntityTreeDisplayModeEnum.EDIT) {
            if (this.enableEntity) {
                if (this.hasSingleConnection && !this.entityNodeDisplayData.hasPath) {
                    disable = element.contextMenuActions == null
                            || (!element.contextMenuActions.includes(ContextMenuActionEnum.CONNECT));
                }
                if (this.entityNode.hasMultipleConnectedParents) {
                    disable = element.contextMenuActions == null
                            || (!element.contextMenuActions.includes(ContextMenuActionEnum.CONNECT)
                                    && !element.contextMenuActions.includes(ContextMenuActionEnum.PUT_IN_PATH));
                } else {
                    if (this.entityNodeDisplayData.hasConnections) {
                        disable = !element.contextMenuActions?.includes(ContextMenuActionEnum.CONNECT);
                    } else {
                        disable = element.contextMenuActions != null
                                && !element.contextMenuActions.includes(ContextMenuActionEnum.PUT_IN_PATH);
                    }
                }
            }
            if (this.entityNode.hasManualSorting) {
                disable = false;
            }
        } else {
            disable = !this.entityNode.isRoot;
        }
        return disable;
    }

    /**
     * Determines if a given pool item drag feature should constrained to a boundary
     */
    enablePoolItemDragBoundary(element: EntityElementModel): boolean {
        let enable = false;

        if (!this.disableDragForPoolItem(element)) {
            if (this.entityNodeDisplayData.hasConnections) {
                enable = this.entityNode.connections?.find((item) => item.code === element.code) != null;
            }
        }
        return enable;
    }

    /**
     * Maps the pool items to {@link DragAndDropGridItemModel};
     */
    getPoolDragAndDropItems(): DragAndDropGridItemModel<EntityElementDropEventModel>[] {
        return this.entityNode.pool?.map((item) => ({
            data: this.getEntityElementDropEventObject(item, EntityElementTypeEnum.POOL),
            disableDrag: this.disableDragForPoolItem(item),
            enableDragBoundary: this.enablePoolItemDragBoundary(item)
        }));
    }

    /**
     * Reorders the pool item elements in the array
     */
    reorderPool(items: DragAndDropGridItemModel<EntityElementDropEventModel>[]): void {
        const newPool = items.map((item) => item.data.value);
        this.changePoolItemsOrder.emit(newPool);
    }

    hasPoolAddButton(): boolean {
        return this.displayMode === EntityTreeDisplayModeEnum.EDIT;
    }

    /**
     * Determines if the drag for a specific connection item should be disabled or not
     * @return boolean
     */
    disableDragForConnectionItem(): boolean {
        return !this.enableEntity;
    }

    getDragItemDataCy(element: EntityElementModel, section: EntityElementTypeEnum): string {
        return `entity-tree-node-${section}-${element.code}-drag-item`;
    }

    getEntityElementDropEventObject(entity: EntityElementModel, originType: EntityElementTypeEnum): EntityElementDropEventModel {
        return {value: entity, origin: originType} as EntityElementDropEventModel;
    }

    openEditModal(element: EntityElementModel, node: EntityTreeBranchModel): void {
        const confirmModalInstance: BusinessSettingsModal = this.modal.open(node.modalType).componentInstance;
        confirmModalInstance.excludedEntities = node.pool;
        confirmModalInstance.isEdit = true;
        confirmModalInstance.setEntity(element);
        confirmModalInstance.entityNode = node;
        confirmModalInstance.entityDisplayData = this.entityNodeDisplayData;
        confirmModalInstance.edit = this.editPoolItem;
        confirmModalInstance.configurationTreeNode = this.configurationTreeNode;
    }

    openAddModal(): void {
        const confirmModalInstance: BusinessSettingsModal = this.modal.open(this.entityNode.modalType).componentInstance;
        confirmModalInstance.excludedEntities = this.entityNode.pool;
        confirmModalInstance.getInitialData();
        confirmModalInstance.entityNode = this.entityNode;
        confirmModalInstance.entityDisplayData = this.entityNodeDisplayData;
        confirmModalInstance.add = this.addToPool;
        confirmModalInstance.add.pipe(take(1))
                .subscribe((element) => {
                    const elementNameModal: string = this.entityNode.displayModalNameFunction != null ?
                            this.entityNode.displayModalNameFunction(element.currentValue) : element.name;
                    element.isANewEntityElement = true;
                    this.toastService.success(
                            `The ${this.entityNode.fullNameLabelText} <strong>${elementNameModal}</strong> was successfully added`,
                            'Success');
                    this.toggleViewMore();
                    // Timeout to wait for the animation be completed to remove the CSS class after 10s
                    timer(this.ADD_ENTITY_ANIMATION_TIMEOUT)
                            .subscribe(() => {
                                element.isANewEntityElement = false;
                            });
                });
    }

    openDeleteModal(element: EntityElementModel, node: EntityTreeBranchModel): void {
        const elementNameModal: string = node.displayModalNameFunction != null ?
                node.displayModalNameFunction(element.currentValue) : element.name;

        const componentInstance: ConfirmModalComponent = this.modal.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        componentInstance.modalConfiguration = {
            title: `Delete ${node.fullNameLabelText}`,
            primaryActionTitle: 'Delete',
            secondaryActionTitle: 'Cancel',
            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
            body: {
                title: `Are you sure you want to delete the ${node.fullNameLabelText} ${elementNameModal}?`,
                manualChecks: {
                    title: 'Be aware that:',
                    options: [`This action will delete this ${node.fullNameLabelText} and remove it from all paths.`]
                } as ConfirmModalManualChecksModel
            } as ConfirmModalBodyModel
        } as ConfirmModalConfigurationModel;
        componentInstance.primaryEvent.subscribe(() => {
            this.removeFromPool.emit(element);
            this.toastService.success(`The ${node.fullNameLabelText} ${elementNameModal} was successfully deleted`,
                    'Success');
        });
    }

    /**
     * Adds an entity to the path node section. Marks the corresponding connection as selected.
     * @param element the {@link EntityElementModel} of the entity to add to the path
     */
    addEntityToPath(element: EntityElementModel): void {
        if (this.entityNodeDisplayData.hasPath) {
            if (this.entityNodeDisplayData.hasConnections && this.entityNode.connections != null) {
                this.entityNode.connections.forEach((item) => {
                    item.isSelected = item.code === element.code;
                });
            }

            // Emit event
            this.addToPathClick.emit(element);
        }
    }

    getButtonLabelArticle(word: string): string {
        return (new RegExp('^[aeiouAEIOU]')).test(word) ? 'an' : 'a';
    }

    /**
     * Toggles the drag and drop message on the path and connections areas
     */
    public dragAndDropMessage(entityTree: EntityTreeBranchModel, typeArea: EntityElementTypeEnum): string {
        let message;
        if (this.displayMode === EntityTreeDisplayModeEnum.VIEW && this.isInUnfinishedPath()) {
            message = this.UNFINISHED_DRAG_DROP_MESSAGE_VIEW_MODE;
        } else {
            if (typeArea === EntityElementTypeEnum.PATH) {
                message = entityTree.dropZoneMessages.path;
            } else {
                message = entityTree.dropZoneMessages.connections;
            }
        }
        return message;
    }

    private entityConnectionsMaxHeight(): number {
        let maxHeight = this.DEFAULT_ENTITY_CONTAINER_MAX_HEIGHT;
        const connectionsMaxHeight = (this.connectionsNumberOfLines() * this.HEIGHT_OF_ONE_ELEMENT) + this.CONTAINERS_PADDING;
        if (connectionsMaxHeight > maxHeight) {
            maxHeight = connectionsMaxHeight;
        }
        return maxHeight;
    }

    private getNumberOfColumns(containerWidth: number): number {
        return Math.floor(((containerWidth - this.CONTAINERS_PADDING) / this.WIDTH_OF_ONE_ELEMENT));
    }

    private getNumberOfLines(elementsLength: number, numberOfColumns: number): number {
        return Math.ceil(elementsLength / numberOfColumns);
    }

    private poolNumberOfLines(): number {
        let numberOfLines = 1;

        if (this.entityPoolContainer != null && this.entityNode.pool != null) {
            // If the entityPoolContainer.nativeElement.clientWidth value has floating points, angular will round it to the nearest integer.
            // In this particular situation, we want the value to be rounded towards zero, so we need to access the actual
            // floating-point width of the element and round it down.
            const poolWidth = Math.floor(this.entityPoolContainer.nativeElement.getBoundingClientRect().width);

            numberOfLines = this.getNumberOfLines(this.poolLength,
                    this.getNumberOfColumns(poolWidth - this.POOL_CONTAINER_MARGIN));
        }
        return numberOfLines;
    }

    private connectionsNumberOfLines(): number {
        let numberOfLines = 1;

        if (this.entityConnectionsContainer != null && this.entityNode.connections != null) {
            const connectionsWidth = Math.floor(this.entityConnectionsContainer.nativeElement.getBoundingClientRect().width);

            numberOfLines = this.getNumberOfLines(this.entityNode.connections.length,
                    this.getNumberOfColumns(connectionsWidth));
        }
        return numberOfLines;
    }

    /**
     * Toggles the view less button on and off
     */
    private toggleViewLess(): void {
        this.hasViewLess = this.isOpen
                && this.poolNumberOfLines() > this.MAX_NUMBER_OF_LINES
                && this.poolNumberOfLines() > this.connectionsNumberOfLines();
        this.isOpen = this.hasViewLess;
    }

    /**
     * Toggles the view more button on and off
     */
    private toggleViewMore(): void {
        this.hasViewMore = !this.isOpen
                && this.poolNumberOfLines() > this.MAX_NUMBER_OF_LINES
                && this.poolNumberOfLines() > this.connectionsNumberOfLines();
    }

    private representableChildParentHasUnfinishedPathOrigin(): boolean {
        return this.entityPathState != null
                && this.entityPathState.parentPathState != null
                && this.entityPathState.parentPathState.unfinishedPath != null
                && this.entityPathState.parentPathState.unfinishedPath.isOrigin;
    }

    private entityHasOnlyConnections(): boolean {
        return !this.entityNodeDisplayData.hasPath && this.entityNodeDisplayData.hasConnections;
    }

    private entityHasSelectedConnections(): boolean {
        return this.entityNode.connections != null && this.entityNode.connections.length > 0;
    }

    private unfinishedPathStateIsNotNull(): boolean {
        return this.entityPathState != null && this.entityPathState.unfinishedPath != null;
    }

    private inactivePathStateIsNotNull(): boolean {
        return this.entityPathState != null && this.entityPathState.inactivePath != null;
    }

    private isInUnfinishedPath(): boolean {
        return this.unfinishedPathStateIsNotNull() && this.entityPathState.unfinishedPath.isInPath;
    }

    private isInInactivePath(): boolean {
        return this.inactivePathStateIsNotNull() && this.entityPathState.inactivePath.isInPath;
    }

    private isInactivePathOrigin(): boolean {
        return this.inactivePathStateIsNotNull() && this.entityPathState.inactivePath.isOrigin;
    }

    private isUnfinishedPathOrigin(): boolean {
        return this.unfinishedPathStateIsNotNull() && this.entityPathState.unfinishedPath.isOrigin;
    }

    private getRepresentableChildPathConnectorClass(baseClass: string): string {
        let pathClass = baseClass;

        if (this.representableChildParentHasUnfinishedPathOrigin()) {
            pathClass += ' unfinished-path-connector';
        } else if (this.isInInactivePath()) {
            pathClass += ' inactive-path-connector';
        }
        return pathClass;
    }

    /**
     * Adds pool item to the connections
     */
    private addEntityToConnections(element: EntityElementModel): void {
        if (this.entityNodeDisplayData.hasConnections) {
            this.addToConnectionsClick.emit(element);
        }
    }

    /**
     * Determines if the entity container height is under the max height
     * @return boolean if the container is under the max height
     */
    private entityContainerIsUnderMaxHeight(): boolean {
        return this.entityPoolContainer.nativeElement.clientHeight <= this.ENTITY_CONTAINER_MAX_HEIGHT;
    }

}

